<script setup>
import { computed, onMounted } from 'vue';
import { useMarketingSectionsStore } from '@/stores/marketing_sections';

/**
 * Renders a configured marketing placement.
 *
 * Renders nothing when the placement is disabled or empty, so the parent can hide
 * the surrounding column.
 */
const props = defineProps({
	/** Placement slug, matching a seeded marketing section. */
	slug: { type: String, required: true },
	/** Widget types this placement must not render, for example on mobile. */
	exclude_types: { type: Array, default: () => [] },
	/** Class applied to the list wrapper, so each placement can keep its own layout. */
	wrapper_class: { type: String, default: '' },
});

const marketingSectionsStore = useMarketingSectionsStore();

const section = computed(() => marketingSectionsStore.getSection(props.slug));
const widgets = computed(() => marketingSectionsStore.getWidgets(props.slug, props.exclude_types));
const registry = computed(() => marketingSectionsStore.component_registry);

onMounted(() => {
	if (!section.value) marketingSectionsStore.fetchSection(props.slug).catch(() => {});
});
</script>

<template>
	<div v-if="widgets.length > 0" class="marketing-section" :class="wrapper_class">
		<section v-for="widget in widgets" :key="widget.id" class="marketing-widget" :data-widget-type="widget.type">
			<div v-if="widget.show_title && widget.title" class="marketing-widget-title">
				<img v-if="widget.media" :src="widget.media.path" alt="" class="marketing-widget-icon">
				<h2>{{ widget.title }}</h2>
			</div>
			<component :is="registry[widget.type]" :widget="widget" />
		</section>
	</div>
</template>

<style scoped>
.marketing-section {
	display: flex;
	flex-direction: column;
	gap: 20px;
	width: 100%;
}
.marketing-widget {
	width: 100%;
	/* Host line heights let a caption crowd the next heading without this. */
	display: flow-root;
}
/* The optional icon sits on the title's line rather than above it. */
.marketing-widget-title {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 10px;
}
.marketing-widget-icon {
	flex: 0 0 auto;
	width: 18px;
	height: 18px;
	object-fit: contain;
}
.marketing-widget-title h2 {
	margin: 0;
	font-size: 16px;
	line-height: 1.4;
	font-weight: 600;
	color: var(--white);
}

/*
 * The live sport sidebar is the one column wide enough for two banners, which is
 * what the page showed before. Below this width it stacks and the widget rule wins.
 */
@media screen and (min-width: 1281px) {
	.marketing-live-sidebar :deep(.marketing-promotion-list) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

/*
 * The game window column is narrow, so cards go two to a row. It is deliberately
 * uncapped: an inner scrollbar on a scrolling page would be a second scrollbar.
 */
.marketing-section.marketing-game-window {
	gap: 16px;
}
.marketing-game-window :deep(.marketing-game-grid),
.marketing-game-window :deep(.marketing-table-grid) {
	grid-template-columns: repeat(2, minmax(0, 1fr));
}
.marketing-game-window :deep(.marketing-promotion-list) {
	grid-template-columns: minmax(0, 1fr);
}
@media screen and (max-width: 991px) {
	.marketing-game-window :deep(.marketing-game-grid),
	.marketing-game-window :deep(.marketing-table-grid),
	.marketing-game-window :deep(.marketing-promotion-list) {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
}
@media screen and (max-width: 767px) {
	.marketing-game-window :deep(.marketing-game-grid),
	.marketing-game-window :deep(.marketing-table-grid),
	.marketing-game-window :deep(.marketing-promotion-list) {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
